import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import HelpTooltip from '../../components/UI/HelpTooltip';
import { contactAPI } from '../../services/api';
import { 
  MapPin, Phone, Mail, Clock, Plus, X, Save, Facebook, Twitter, Linkedin, Instagram, Youtube
} from 'lucide-react';

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 1000px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const SettingsCard = styled(Card)`
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.theme.typography.fontSize.base};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const DynamicList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DynamicListItem = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const RemoveButton = styled.button`
  padding: 0.5rem;
  background: ${props => props.theme.colors.error}15;
  color: ${props => props.theme.colors.error};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme.colors.error};
    color: white;
  }
`;

const AddButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.theme.colors.primary}15;
  color: ${props => props.theme.colors.primary};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme.colors.primary}25;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1.5rem;
`;

const ContactSettings = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const [settings, setSettings] = useState({
    address: '',
    city: '',
    country: '',
    po_box: '',
    landmark: '',
    phone_numbers: [''],
    emails: [''],
    office_hours: '',
    social_media: {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      youtube: '',
    },
    additional_info: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await contactAPI.getSettings();
      setSettings({
        address: response.data.address || '',
        city: response.data.city || '',
        country: response.data.country || '',
        po_box: response.data.po_box || '',
        landmark: response.data.landmark || '',
        phone_numbers: response.data.phone_numbers || [''],
        emails: response.data.emails || [''],
        office_hours: response.data.office_hours || '',
        social_media: response.data.social_media || {
          facebook: '',
          twitter: '',
          linkedin: '',
          instagram: '',
          youtube: '',
        },
        additional_info: response.data.additional_info || '',
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: value
      }
    }));
  };

  const addPhone = () => {
    setSettings(prev => ({
      ...prev,
      phone_numbers: [...prev.phone_numbers, '']
    }));
  };

  const removePhone = (index) => {
    setSettings(prev => ({
      ...prev,
      phone_numbers: prev.phone_numbers.filter((_, i) => i !== index)
    }));
  };

  const updatePhone = (index, value) => {
    setSettings(prev => ({
      ...prev,
      phone_numbers: prev.phone_numbers.map((phone, i) => i === index ? value : phone)
    }));
  };

  const addEmail = () => {
    setSettings(prev => ({
      ...prev,
      emails: [...prev.emails, '']
    }));
  };

  const removeEmail = (index) => {
    setSettings(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  const updateEmail = (index, value) => {
    setSettings(prev => ({
      ...prev,
      emails: prev.emails.map((email, i) => i === index ? value : email)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Filter out empty phone numbers and emails
      const cleanedSettings = {
        ...settings,
        phone_numbers: settings.phone_numbers.filter(phone => phone.trim() !== ''),
        emails: settings.emails.filter(email => email.trim() !== ''),
      };

      await contactAPI.updateSettings(cleanedSettings);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(error.response?.data?.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SettingsContainer>
        <Card style={{ padding: '3rem', textAlign: 'center' }}>
          Loading contact settings...
        </Card>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer>
      <Header>
        <Title>
          Contact Settings
          <HelpTooltip
            steps={[
              {
                title: 'Step 1 – Basic contact details',
                content: (
                  <ul>
                    <li>Enter the office address, city, country and landmark that will be shown on the public contact page.</li>
                    <li>Add the main phone numbers and email addresses that the public should use.</li>
                  </ul>
                ),
              },
              {
                title: 'Step 2 – Office hours and social media',
                content: (
                  <ul>
                    <li>Fill in office hours so people know when they can expect a response.</li>
                    <li>Add official social media links (Facebook, Twitter, LinkedIn, etc.) for the organization.</li>
                  </ul>
                ),
              },
              {
                title: 'Step 3 – Additional notes',
                content: (
                  <p>
                    Use the “Additional Information” section for any special instructions, such as emergency contacts or
                    preferred language for communication.
                  </p>
                ),
              },
            ]}
          />
        </Title>
      </Header>

      {success && (
        <SuccessMessage>
          <Save size={20} />
          Contact settings saved successfully!
        </SuccessMessage>
      )}

      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}

      <form onSubmit={handleSubmit}>
        {/* Address Information */}
        <SettingsCard theme={theme}>
          <SectionTitle theme={theme}>
            <MapPin size={24} />
            Address Information
          </SectionTitle>

          <Grid>
            <FormGroup>
              <Label theme={theme}>Address</Label>
              <Input
                value={settings.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Street address"
              />
            </FormGroup>

            <FormGroup>
              <Label theme={theme}>City</Label>
              <Input
                value={settings.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="City"
              />
            </FormGroup>

            <FormGroup>
              <Label theme={theme}>Country</Label>
              <Input
                value={settings.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                placeholder="Country"
              />
            </FormGroup>

            <FormGroup>
              <Label theme={theme}>P.O. Box</Label>
              <Input
                value={settings.po_box}
                onChange={(e) => handleInputChange('po_box', e.target.value)}
                placeholder="P.O. Box 12345"
              />
            </FormGroup>
          </Grid>

          <FormGroup>
            <Label theme={theme}>Landmark</Label>
            <Input
              value={settings.landmark}
              onChange={(e) => handleInputChange('landmark', e.target.value)}
              placeholder="Near Meskel Square"
            />
          </FormGroup>
        </SettingsCard>

        {/* Phone Numbers */}
        <SettingsCard theme={theme}>
          <SectionTitle theme={theme}>
            <Phone size={24} />
            Phone Numbers
          </SectionTitle>

          <DynamicList>
            {settings.phone_numbers.map((phone, index) => (
              <DynamicListItem key={index}>
                <Input
                  value={phone}
                  onChange={(e) => updatePhone(index, e.target.value)}
                  placeholder="+251 XX XXX XXXX"
                  style={{ flex: 1 }}
                />
                {settings.phone_numbers.length > 1 && (
                  <RemoveButton
                    type="button"
                    onClick={() => removePhone(index)}
                    theme={theme}
                  >
                    <X size={16} />
                  </RemoveButton>
                )}
              </DynamicListItem>
            ))}
          </DynamicList>

          <div style={{ marginTop: '1rem' }}>
            <AddButton type="button" onClick={addPhone} theme={theme}>
              <Plus size={16} /> Add Phone Number
            </AddButton>
          </div>
        </SettingsCard>

        {/* Email Addresses */}
        <SettingsCard theme={theme}>
          <SectionTitle theme={theme}>
            <Mail size={24} />
            Email Addresses
          </SectionTitle>

          <DynamicList>
            {settings.emails.map((email, index) => (
              <DynamicListItem key={index}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => updateEmail(index, e.target.value)}
                  placeholder="email@example.com"
                  style={{ flex: 1 }}
                />
                {settings.emails.length > 1 && (
                  <RemoveButton
                    type="button"
                    onClick={() => removeEmail(index)}
                    theme={theme}
                  >
                    <X size={16} />
                  </RemoveButton>
                )}
              </DynamicListItem>
            ))}
          </DynamicList>

          <div style={{ marginTop: '1rem' }}>
            <AddButton type="button" onClick={addEmail} theme={theme}>
              <Plus size={16} /> Add Email Address
            </AddButton>
          </div>
        </SettingsCard>

        {/* Office Hours */}
        <SettingsCard theme={theme}>
          <SectionTitle theme={theme}>
            <Clock size={24} />
            Office Hours
          </SectionTitle>

          <FormGroup>
            <Label theme={theme}>Office Hours</Label>
            <Input
              value={settings.office_hours}
              onChange={(e) => handleInputChange('office_hours', e.target.value)}
              placeholder="Monday - Friday: 8:00 AM - 5:00 PM"
            />
          </FormGroup>
        </SettingsCard>

        {/* Social Media */}
        <SettingsCard theme={theme}>
          <SectionTitle theme={theme}>
            Social Media Links
          </SectionTitle>

          <Grid>
            <FormGroup>
              <Label theme={theme}>
                <Facebook size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Facebook
              </Label>
              <Input
                type="url"
                value={settings.social_media.facebook}
                onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                placeholder="https://facebook.com/yourpage"
              />
            </FormGroup>

            <FormGroup>
              <Label theme={theme}>
                <Twitter size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Twitter
              </Label>
              <Input
                type="url"
                value={settings.social_media.twitter}
                onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                placeholder="https://twitter.com/yourhandle"
              />
            </FormGroup>

            <FormGroup>
              <Label theme={theme}>
                <Linkedin size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                LinkedIn
              </Label>
              <Input
                type="url"
                value={settings.social_media.linkedin}
                onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/company/yourcompany"
              />
            </FormGroup>

            <FormGroup>
              <Label theme={theme}>
                <Instagram size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Instagram
              </Label>
              <Input
                type="url"
                value={settings.social_media.instagram}
                onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                placeholder="https://instagram.com/yourhandle"
              />
            </FormGroup>

            <FormGroup>
              <Label theme={theme}>
                <Youtube size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                YouTube
              </Label>
              <Input
                type="url"
                value={settings.social_media.youtube}
                onChange={(e) => handleSocialMediaChange('youtube', e.target.value)}
                placeholder="https://youtube.com/@yourchannel"
              />
            </FormGroup>
          </Grid>
        </SettingsCard>

        {/* Additional Information */}
        <SettingsCard theme={theme}>
          <SectionTitle theme={theme}>
            Additional Information
          </SectionTitle>

          <FormGroup>
            <Label theme={theme}>Additional Information</Label>
            <TextArea
              value={settings.additional_info}
              onChange={(e) => handleInputChange('additional_info', e.target.value)}
              placeholder="Any additional information to display on the contact page..."
              theme={theme}
            />
          </FormGroup>
        </SettingsCard>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <Button
            type="button"
            variant="outline"
            onClick={fetchSettings}
            disabled={saving}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="large"
            disabled={saving}
          >
            <Save size={20} style={{ marginRight: '0.5rem' }} />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </SettingsContainer>
  );
};

export default ContactSettings;

















